import { type Course, DAY, SECOND, UserAction } from "@brookbush/types";

import { useAuth, useUser } from "@clerk/nextjs";
import { getCookie, setCookie } from "cookies-next";
import { motion } from "framer-motion";
import { ChevronRightIcon } from "lucide-react";
import { useRouter } from "next/compat/router";
import Image from "next/image";
import { useEffect } from "react";

import Button from "~/components/Button";
import Dialog from "~/components/Dialog";
import { useUserAction } from "~/hooks";
import usePracticeExamAttempts from "~/hooks/usePracticeExamAttempts";

interface CourseModalProps {
	course: Course;
}

const VISITED_COURSE_COOKIE_PREFIX = "visited-course";

const STEPS = [
	{
		title: "Start with the practice exam:",
		description:
			"Do not read a single thing yet. Jump straight into the practice test to establish your baseline.",
	},
	{
		title: "Get a Custom Study Plan:",
		description:
			"Our Exam Coach instantly analyzes your results and generates a highly personalized study guide.",
	},
	{
		title: "Targeted Highlighting:",
		description:
			"The coach highlights the exact course sections you need to review based on the questions you missed. You never waste time reading material you already know.",
	},
];

const CourseConversionModal = ({ course }: CourseModalProps) => {
	const { userAction, resetUserAction, setUserAction } = useUserAction();
	const { isLoaded, userId } = useAuth();
	const { user } = useUser();
	const { pastAttempts, isLoading: isLoadingAttempts } =
		usePracticeExamAttempts();
	const isOpen = userAction === UserAction.START_PRACTICE_EXAM;
	const router = useRouter();

	const tutorialStatus = (user?.unsafeMetadata as any)?.onboarding
		?.courseTutorial;
	const isTutorialPending =
		!!userId && tutorialStatus !== "finished" && tutorialStatus !== "skipped";

	useEffect(() => {
		const cookieKey = `${VISITED_COURSE_COOKIE_PREFIX}:${course.slug}`;

		if (
			!isLoaded ||
			isLoadingAttempts ||
			!!pastAttempts?.length ||
			getCookie(cookieKey) ||
			isTutorialPending ||
			userAction
		)
			return;

		const timeoutId = setTimeout(() => {
			setUserAction(UserAction.START_PRACTICE_EXAM);
			setCookie(cookieKey, "true", { maxAge: (30 * DAY) / 1000 });
		}, 3 * SECOND);

		return () => clearTimeout(timeoutId);
	}, [
		course.slug,
		isLoaded,
		isLoadingAttempts,
		pastAttempts,
		setUserAction,
		isTutorialPending,
		userAction,
	]);

	return (
		<Dialog
			isOpen={isOpen}
			onClose={resetUserAction}
			layout
			className="max-w-xl overflow-hidden"
		>
			<motion.div
				layout
				className="flex w-full flex-col items-center gap-6 px-2 pb-2"
			>
				<div className="flex flex-col items-center gap-4">
					<motion.div
						layout="position"
						className="flex size-16 items-center justify-center rounded-2xl dark:bg-slate-800"
					>
						<Image
							priority
							fetchPriority="high"
							src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/brookbush-institute-logo-icon-primary.png`}
							alt="Brookbush Institute Logo"
							width={64}
							height={64}
							className="size-24 object-contain"
						/>
					</motion.div>
					<motion.h2
						layout="position"
						className="w-70 text-center font-bold text-2xl text-slate-900 md:w-[320px] dark:text-white"
					>
						Start Here!
					</motion.h2>
				</div>

				<motion.div
					layout
					className="flex w-full max-w-lg flex-col gap-6 text-slate-700 dark:text-slate-300"
				>
					<div className="flex flex-col gap-4">
						{STEPS.map((step, index) => (
							<div key={step.title} className="flex gap-3">
								<span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-white">
									{index + 1}
								</span>
								<div className="flex flex-col">
									<p className="font-bold text-slate-900 dark:text-white">
										{step.title}
									</p>
									<p>{step.description}</p>
								</div>
							</div>
						))}
					</div>

					<div className="flex flex-col items-center gap-4">
						<Button
							size="lg"
							className="w-full font-medium"
							onClick={async () => {
								router?.push(`/courses/${course.slug}/exams/practice`);
								resetUserAction();
							}}
						>
							Start Practice Exam
						</Button>

						<Button
							variant={null}
							size="sm"
							onClick={resetUserAction}
							className="group gap-1 hover:text-slate-700 hover:underline dark:hover:text-slate-300"
						>
							Continue to course
							<ChevronRightIcon
								size={16}
								className="transition-transform group-hover:translate-x-0.5"
							/>
						</Button>
					</div>
				</motion.div>
			</motion.div>
		</Dialog>
	);
};

export default CourseConversionModal;
